import React, { useState } from 'react';
import { useTheme } from '../theme/ThemeContext';

interface SettingsModalProps {
  soundEnabled: boolean;
  hapticsEnabled: boolean;
  onToggleSound: (enabled: boolean) => void;
  onToggleHaptics: (enabled: boolean) => void;
  onResetGame: () => void;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  soundEnabled,
  hapticsEnabled,
  onToggleSound,
  onToggleHaptics,
  onResetGame,
  onClose,
}) => {
  const { themeMode, theme, setThemeMode } = useTheme();
  const [confirmReset, setConfirmReset] = useState(false);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '24px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '380px',
          backgroundColor: theme.isDark ? '#18181b' : '#ffffff',
          borderRadius: '24px',
          padding: '28px',
          border: `1px solid ${theme.isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          color: theme.textColor,
          fontFamily: '"Outfit", sans-serif',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>Settings</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: theme.textColor,
              cursor: 'pointer',
              opacity: 0.7,
            }}
          >
            ✕
          </button>
        </div>

        {/* Appearance Section */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ fontSize: '13px', fontWeight: 700, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Appearance
          </label>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button
              onClick={() => setThemeMode('dark')}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '12px',
                backgroundColor: themeMode === 'dark' ? '#000000' : 'transparent',
                border: `1.5px solid ${themeMode === 'dark' ? '#3b82f6' : theme.isDark ? '#27272a' : '#e4e4e7'}`,
                color: themeMode === 'dark' ? '#ffffff' : theme.textColor,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              🌙 Dark
            </button>
            <button
              onClick={() => setThemeMode('light')}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '12px',
                backgroundColor: themeMode === 'light' ? '#f5f5f0' : 'transparent',
                border: `1.5px solid ${themeMode === 'light' ? '#3b82f6' : theme.isDark ? '#27272a' : '#e4e4e7'}`,
                color: themeMode === 'light' ? '#18181b' : theme.textColor,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              ☀️ Light
            </button>
          </div>
        </div>

        {/* Audio & Feedback Section */}
        <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <label style={{ fontSize: '13px', fontWeight: 700, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Audio & Haptics
          </label>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>🔊 Sound Effects</span>
            <button
              onClick={() => onToggleSound(!soundEnabled)}
              style={{
                padding: '6px 14px',
                borderRadius: '9999px',
                backgroundColor: soundEnabled ? '#22c55e' : theme.isDark ? '#27272a' : '#e4e4e7',
                border: 'none',
                color: soundEnabled ? '#ffffff' : theme.textColor,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {soundEnabled ? 'ON' : 'OFF'}
            </button>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>📳 Haptic Feedback</span>
            <button
              onClick={() => onToggleHaptics(!hapticsEnabled)}
              style={{
                padding: '6px 14px',
                borderRadius: '9999px',
                backgroundColor: hapticsEnabled ? '#22c55e' : theme.isDark ? '#27272a' : '#e4e4e7',
                border: 'none',
                color: hapticsEnabled ? '#ffffff' : theme.textColor,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {hapticsEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>

        {/* Reset Game Section */}
        <div style={{ borderTop: `1px solid ${theme.isDark ? '#27272a' : '#e4e4e7'}`, paddingTop: '20px' }}>
          {!confirmReset ? (
            <button
              onClick={() => setConfirmReset(true)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '12px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid #ef4444',
                color: '#ef4444',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              ⚠️ Reset Game
            </button>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#ef4444', fontWeight: 600 }}>
                Permanently delete all progress and start a new seed?
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setConfirmReset(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.isDark ? '#3f3f46' : '#d4d4d8'}`,
                    backgroundColor: 'transparent',
                    color: theme.textColor,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setConfirmReset(false);
                    onResetGame();
                    onClose();
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#ef4444',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Confirm Reset
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: '20px', textAlign: 'center', opacity: 0.5, fontSize: '12px' }}>
          Expanding Minefield v1.0.0 • Offline SQLite
        </div>
      </div>
    </div>
  );
};
